<template>
  <div class="kt-login-v2__container">
    <ol class="step-indicator">
      <li
        v-for="step in navigationSteps"
        :key="step.id"
        :class="{
          'active': page === step.id
        }"
      >
        <div class="step">
          <i :class="step.icon_class"/>
        </div>
        <div class="caption hidden-xs hidden-sm">
          <span v-text="step.title"/>
        </div>
      </li>
    </ol>

    <two-factor-setup-intro v-if="page === AuthPage.TWO_FACTOR_AUTH_INTRO"/>
    <two-factor-setup-info v-if="page === AuthPage.TWO_FACTOR_AUTH_INFO"/>
    <two-factor-setup-form v-else-if="page === AuthPage.TWO_FACTOR_AUTH_SETUP"/>

    <form
      action="/logout"
      method="GET"
      class="pl-4 pb-4"
    >
      <button
        type="submit"
        class="btn btn-light"
      >
        <span style="color: #333333">Uitloggen</span>
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { AuthPage, useAuthStore } from '@/stores/auth'
import TwoFactorSetupForm from '@/auth/components/TwoFactorSetupForm.vue'
import TwoFactorSetupIntro from '@/auth/components/TwoFactorSetupIntro.vue'
import { useEventListener } from '@vueuse/core'
import TwoFactorSetupInfo from '@/auth/components/TwoFactorSetupInfo.vue'

const props = defineProps<{
  hasUser: boolean;
}>()

const authStore = useAuthStore()

const {
  page,
  userTwoFactorEnabled,
  twoFactorConfirmed,
  userCompletedTwoFactorAuth,
  navigationSteps
} = storeToRefs(authStore)

onBeforeMount(async () => {
  if (!props.hasUser) return window.location.href = '/'

  await authStore.checkTwoFactorAuthEnabled()

  if (userTwoFactorEnabled.value) {
    // Skip intro if 2FA is already enabled but not accepted
    if (!twoFactorConfirmed.value) return page.value = AuthPage.TWO_FACTOR_AUTH_INFO

    // Redirect to home if 2FA is already enabled
    return window.location.href = '/'
  }

  page.value = AuthPage.TWO_FACTOR_AUTH_INTRO
})

onMounted(() => {
  useEventListener(window, 'beforeunload', (e) => {
    if (!userCompletedTwoFactorAuth.value) {
      e.preventDefault()
    }
  })

  useEventListener(window, 'unload', () => {
    authStore.resetTwoFactorAuth()
  })
})
</script>

<style scoped lang="scss">
@use "sass:math";

::v-deep(button.btn--primary) {
  padding: 1rem 2rem !important;
}

::v-deep(.kt-login-v2__actions .btn) {
  font-size: 1.1rem !important;
  position: absolute;
  bottom: 2rem;
  right: 2rem;
}

.kt-login-v2__container {
  position: relative;
  min-width: 1000px !important;
  max-width: 1000px !important;
  width: unset !important;
  margin: 0 auto;
}

::v-deep(.kt-login-v2__title h3) {
  margin-top: 0 !important;
  margin-bottom: 1rem !important;
}

$wizard-color-neutral: #CCC !default;
$wizard-color-active: #00606f !default;
$wizard-step-width-height: 44px !default;
$wizard-step-font-size: 22px !default;

.step-wrapper {
  padding: 20px 0;
  display: none;

  &.active {
    display: block;
  }
}

.step-indicator {
  border-collapse: separate;
  display: table;
  margin-left: 0;
  position: relative;
  table-layout: fixed;
  text-align: center;
  vertical-align: middle;
  padding-left: 0;
  padding-top: 20px;

  li {
    display: table-cell;
    position: relative;
    float: none;
    padding: 0;
    width: 1%;

    &:after {
      background-color: $wizard-color-neutral;
      content: "";
      display: block;
      height: 1px;
      position: absolute;
      width: 100%;
      top: math.div($wizard-step-width-height, 2);
    }

    &:after {
      left: 50%;
    }

    &:last-child {
      &:after {
        display: none;
      }
    }

    &.active {
      .step {
        border-color: $wizard-color-active;
        color: $wizard-color-active;
      }

      .caption {
        color: $wizard-color-active;
        font-weight: bolder;
      }
    }
  }

  .step {
    background-color: #fff;
    border-radius: 50%;
    border: 1px solid $wizard-color-neutral;
    color: $wizard-color-neutral;
    font-size: $wizard-step-font-size;
    height: $wizard-step-width-height;
    line-height: $wizard-step-width-height;
    margin: 0 auto;
    position: relative;
    width: $wizard-step-width-height;
    z-index: 1;
  }

  .caption {
    color: $wizard-color-neutral;
    padding: 11px 16px;
  }
}
</style>
